import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { api } from '@/services/api';
import { setUserId } from '@amplitude/analytics-browser';
import { trackEvent } from '@/lib/amplitude';
import { STORAGE_KEY } from '../lib/anonymous-session';
import type { RegisterParams } from '@/services/api/auth/auth.types';

const TOKEN_KEY = 'auth_token';
const REFRESH_KEY = 'auth_refresh_token';
const USER_KEY = 'auth_user';

interface User {
  [key: string]: any;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  isNewUser: boolean;
  login: (data: { email: string; password: string }) => Promise<void>;
  register: (data: RegisterParams) => Promise<string>;
  googleLogin: (googleToken: string, options: { allowSignup: boolean; marketingOptIn: boolean; productEmailOptOut: boolean }) => Promise<void>;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isNewUser, setIsNewUser] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Restore auth state from localStorage
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    const userStr = localStorage.getItem(USER_KEY);
    if (token && userStr) {
      try {
        setUser(JSON.parse(userStr));
      } catch {
        setUser(null);
        localStorage.removeItem(USER_KEY);
      }
    }
    setLoading(false);
  }, []);

  const saveAuth = (user: User, token: string, refreshToken: string, isNew: boolean) => {
    setUser(user);
    setIsNewUser(isNew);
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(REFRESH_KEY, refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  };

  const clearAuth = () => {
    setUser(null);
    setIsNewUser(false);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);

    localStorage.removeItem(STORAGE_KEY)
  };

  const login = async (data: { email: string; password: string }) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.auth.login(data);

      const { id, email } = res.user;
      console.log(id, email);

      trackEvent('Login successful', {
        email: email,
        id,
      });

      setUserId(id);

      localStorage.removeItem(STORAGE_KEY)

      saveAuth(res.user, res.token, res.refreshToken, false);
    } catch (e: any) {
      const errorMessage = e?.response?.data?.message || e?.message || 'Login failed';
      setError(errorMessage);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  const register = async (data: RegisterParams) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.auth.register(data);
      // Возвращаем сообщение для отображения пользователю
      return res.message || 'Registration successful. Please check your email.';
    } catch (e: any) {
      const errorMessage = e?.response?.data?.message || e?.message || 'Registration failed';
      setError(errorMessage);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = async (googleToken: string, options: { allowSignup: boolean; marketingOptIn: boolean; productEmailOptOut: boolean }) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.auth.googleAuth({ googleToken, ...options });

      trackEvent('Google login successful', {
        email: result.user.email,
        id: result.user.id,
      });

      setUserId(result.user.id);

      saveAuth(result.user, result.token, result.refreshToken, result.isNewUser || false);
    } catch (e: any) {
      const errorMessage = e?.response?.data?.message || e?.message || 'Google auth failed';
      setError(errorMessage);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    clearAuth();
  };

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated: !!user, loading, error, isNewUser, login, register, googleLogin, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
